import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import { parseTaskText, type TaskBlock } from '@/lib/taskText';

/**
 * Текст задания с разметкой.
 *
 * Бэкенд присылает задание сплошной строкой. Здесь номер блока становится
 * отдельной меткой, вопросы внутри блока — списком, а перечисления через тире —
 * пунктами: на телефоне это единственный способ не потерять, на что отвечаешь.
 *
 * Текст не переписывается и не сокращается — меняется только подача.
 */
export function TaskText({ text, className }: { text?: string | null; className?: string }) {
  // Текст задания может не прийти: пустой экран лучше упавшего
  const blocks = useMemo(() => parseTaskText(text ?? ''), [text]);

  return (
    <div className={cn('grid min-w-0 gap-3 text-[12.5px] leading-[1.55] [overflow-wrap:anywhere]', className)}>
      {blocks.map((block, index) => (
        // Соседние пункты нумерованного списка сдвигаются друг к другу:
        // одинаковый отступ разрывал бы список на отдельные абзацы
        <Block key={index} block={block} tight={isTightStep(block) && isTightStep(blocks[index - 1])} />
      ))}
    </div>
  );
}

function isTightStep(block: TaskBlock | undefined): boolean {
  return block?.kind === 'step' && block.items.length === 0;
}

function Block({ block, tight }: { block: TaskBlock; tight?: boolean }) {
  if (block.kind === 'text') return <p className="whitespace-pre-line">{block.text}</p>;

  if (block.kind === 'term') {
    return (
      <p>
        {/* Термин отделён от объяснения: сплошным абзацем такой текст не читается */}
        <strong className="block font-medium">{block.title}</strong>
        <span>{block.text}</span>
      </p>
    );
  }

  if (block.kind === 'list') {
    return (
      <div>
        {block.title ? <p className="mb-1.5 font-medium">{block.title}</p> : null}
        <Items items={block.items} />
      </div>
    );
  }

  // Пункт без вопросов — обычный элемент нумерованного списка: рамка вокруг
  // одной строки только утяжелила бы текст
  if (block.items.length === 0) {
    return (
      <p className={cn('flex gap-2', tight && '-mt-[7px]')}>
        <span className="shrink-0 font-medium tabular-nums text-accent">{block.number}.</span>
        <span>{block.title}</span>
      </p>
    );
  }

  return (
    <div className="rounded-control border border-line/70 bg-surface-2/60 p-2.5">
      <div className="flex items-baseline gap-2">
        <span className="shrink-0 font-medium tabular-nums text-accent">{block.number}.</span>
        <strong className="font-medium">{block.title}</strong>
      </div>
      <Items items={block.items} className="mt-2 pl-[19px]" />
    </div>
  );
}

function Items({ items, className }: { items: string[]; className?: string }) {
  return (
    <ul className={cn('grid gap-1.5', className)}>
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <span className="mt-[7px] h-[3px] w-[3px] shrink-0 rounded-full bg-brand" aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
